import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  FileText,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  Receipt,
  Zap,
  Clock,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import Swal from "sweetalert2";
import "./CapitalGainCalculator.css";

const GAIN_TYPES = [
  {
    value: "stcg",
    label: "STCG",
    fullLabel: "Short-Term Capital Gain",
    desc: "Held for less than 12 months",
    color: "#f59e0b",
    colorEnd: "#fcd34d",
    Icon: Zap,
  },
  {
    value: "ltcg",
    label: "LTCG",
    fullLabel: "Long-Term Capital Gain",
    desc: "Held for 12 months or more",
    color: "#10b981",
    colorEnd: "#6ee7b7",
    Icon: Clock,
  },
];

const formatINR = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CapitalGainCalculator() {
  const [gainType, setGainType] = useState("stcg");
  const [purchase, setPurchase] = useState("");
  const [sale, setSale] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [otherCost, setOtherCost] = useState("");
  const [result, setResult] = useState(null);

  const active = GAIN_TYPES.find((g) => g.value === gainType);
  const accentColor = active?.color || "#10b981";
  const accentEnd = active?.colorEnd || "#6ee7b7";

  const handleCalculate = () => {
    if (!purchase || !sale) {
      Swal.fire({
        title: "Missing Details",
        text: "Please enter at least the Purchase Value and Sale Value.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: accentColor,
        width: 380,
      });
      return;
    }

    const purchaseVal = Number(purchase);
    const saleVal = Number(sale);
    const b = Number(brokerage) || 0;
    const o = Number(otherCost) || 0;

    const totalCost = purchaseVal + b + o;
    const capitalGain = Math.round(saleVal - totalCost);
    const isProfit = capitalGain >= 0;

    // Tax computation
    let taxAmount = 0;
    let taxRate = 0;
    if (gainType === "stcg") {
      taxRate = 20; // flat 20% STCG on equity (post-Jul 2024 budget)
      taxAmount = Math.round((Math.max(0, capitalGain) * taxRate) / 100);
    } else {
      // LTCG: 12.5% above ₹1.25 lakh exemption
      taxRate = 12.5;
      const exemption = 125000;
      const taxableGain = Math.max(0, capitalGain - exemption);
      taxAmount = Math.round((taxableGain * taxRate) / 100);
    }

    const netGain = capitalGain - taxAmount;

    // Generate Scenario Breakdown Table (Fluctuating Sale Price)
    const scenarios = [];
    const steps = [-20, -10, 0, 10, 20, 30, 40, 50]; // % change in sale value

    steps.forEach((p) => {
      const simSale = saleVal * (1 + p / 100);
      const simGain = Math.round(simSale - totalCost);
      let simTax = 0;

      if (gainType === "stcg") {
        simTax = Math.round((Math.max(0, simGain) * taxRate) / 100);
      } else {
        const taxableGain = Math.max(0, simGain - 125000);
        simTax = Math.round((taxableGain * taxRate) / 100);
      }

      scenarios.push({
        percent: p,
        salePrice: Math.round(simSale),
        gain: simGain,
        tax: simTax,
        net: simGain - simTax,
        isCurrent: p === 0,
      });
    });

    setResult({
      capitalGain,
      totalCost,
      saleVal,
      taxAmount,
      taxRate,
      netGain,
      isProfit,
      scenarios,
      gainPct:
        purchaseVal > 0
          ? ((capitalGain / purchaseVal) * 100).toFixed(2)
          : "0.00",
    });
  };

  const handleReset = () => {
    setPurchase("");
    setSale("");
    setBrokerage("");
    setOtherCost("");
    setResult(null);
  };

  return (
    <div className="cg-wrapper">
      <Navbar />

      {/* ── Hero ── */}
      <section className="cg-hero">
        <div className="cg-hero-bg" />
        <div className="cg-hero-dots" />
        <div className="cg-hero-glow cg-glow-l" />
        <div className="cg-hero-glow cg-glow-r" />

        <div className="cg-hero-inner">
          <Link to="/calculators" className="cg-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="cg-hero-top">
            <div>
              <div className="cg-eyebrow">
                <FileText size={12} /> Capital Gains &amp; Tax
              </div>
              <h1 className="cg-hero-title">
                Know Your Tax on
                <br />
                <span
                  className="cg-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  Every Investment Gain
                </span>
              </h1>
              <p className="cg-hero-sub">
                Compute STCG and LTCG tax liability on your equity, mutual fund,
                or property sale — and optimise your tax outgo.
              </p>
            </div>

            <div
              className="cg-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {active ? (
                <active.Icon size={36} color="#fff" />
              ) : (
                <Receipt size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Gain type chips */}
          <div className="cg-type-chips">
            {GAIN_TYPES.map(
              ({ value, label, fullLabel, desc, color, Icon }) => (
                <button
                  key={value}
                  className={`cg-chip${gainType === value ? " cg-chip--active" : ""}`}
                  style={
                    gainType === value
                      ? { borderColor: color, background: `${color}14`, color }
                      : {}
                  }
                  onClick={() => {
                    setGainType(value);
                    setResult(null);
                  }}
                >
                  <Icon size={13} />
                  <span>{label}</span>
                  <span className="cg-chip-sub">{desc}</span>
                </button>
              ),
            )}
          </div>
        </div>
      </section>

      {/* ── Main ── */}
      <div className="cg-content">
        {/* Input Card */}
        <div className="cg-card">
          <div
            className="cg-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
            }}
          />
          <div className="cg-card-body">
            {/* Active type pill */}
            <div
              className="cg-active-pill"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}22`,
                color: accentColor,
              }}
            >
              {active && <active.Icon size={13} />}
              {active?.fullLabel} — {active?.desc}
            </div>

            <div className="cg-section-title">
              Transaction Details
              <span className="cg-section-line" />
            </div>

            <div className="cg-grid-2">
              {/* Purchase Value */}
              <div className="cg-field">
                <label className="cg-label">
                  <IndianRupee size={12} /> Purchase / Cost Value (₹)
                </label>
                <div className="cg-input-wrap">
                  <span className="cg-input-icon">
                    <IndianRupee size={14} />
                  </span>
                  <input
                    type="number"
                    className="cg-input"
                    placeholder="e.g., 200000"
                    value={purchase}
                    onChange={(e) => setPurchase(e.target.value)}
                  />
                </div>
              </div>

              {/* Sale Value */}
              <div className="cg-field">
                <label className="cg-label">
                  <IndianRupee size={12} /> Sale Value (₹)
                </label>
                <div className="cg-input-wrap">
                  <span className="cg-input-icon">
                    <IndianRupee size={14} />
                  </span>
                  <input
                    type="number"
                    className="cg-input"
                    placeholder="e.g., 250000"
                    value={sale}
                    onChange={(e) => setSale(e.target.value)}
                  />
                </div>
              </div>

              {/* Brokerage */}
              <div className="cg-field">
                <label className="cg-label">
                  <Receipt size={12} /> Brokerage / Charges (₹)
                </label>
                <div className="cg-input-wrap">
                  <span className="cg-input-icon">
                    <Receipt size={14} />
                  </span>
                  <input
                    type="number"
                    className="cg-input"
                    placeholder="e.g., 500 (optional)"
                    value={brokerage}
                    onChange={(e) => setBrokerage(e.target.value)}
                  />
                </div>
              </div>

              {/* Other Costs */}
              <div className="cg-field">
                <label className="cg-label">
                  <FileText size={12} /> Other Costs (₹)
                </label>
                <div className="cg-input-wrap">
                  <span className="cg-input-icon">
                    <FileText size={14} />
                  </span>
                  <input
                    type="number"
                    className="cg-input"
                    placeholder="e.g., 200 (optional)"
                    value={otherCost}
                    onChange={(e) => setOtherCost(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="cg-btn-row">
              <button
                className="cg-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
                onClick={handleCalculate}
              >
                <Calculator size={16} /> Calculate Gain
              </button>
              <button className="cg-reset-btn" onClick={handleReset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {result && (
          <>
            <div className="cg-card">
              <div
                className="cg-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="cg-card-body">
                <div className="cg-section-title">
                  Capital Gain Summary
                  <span className="cg-section-line" />
                </div>

                {/* Profit / Loss banner */}
                <div
                  className={`cg-status-banner${result.isProfit ? " cg-status-banner--profit" : " cg-status-banner--loss"}`}
                >
                  {result.isProfit ? (
                    <TrendingUp size={18} />
                  ) : (
                    <TrendingDown size={18} />
                  )}
                  <span>
                    This transaction resulted in a{" "}
                    <strong>{result.isProfit ? "Profit" : "Loss"}</strong> of{" "}
                    <strong>₹ {formatINR(Math.abs(result.capitalGain))}</strong>{" "}
                    ({result.gainPct}%{result.isProfit ? " gain" : " loss"} on
                    cost)
                  </span>
                </div>

                {/* Result cards */}
                <div className="cg-result-grid">
                  {[
                    {
                      label: "Capital Gain / Loss",
                      value: result.capitalGain,
                      color: result.isProfit ? accentColor : "#ef4444",
                      prefix: result.capitalGain < 0 ? "-₹ " : "₹ ",
                      displayVal: formatINR(Math.abs(result.capitalGain)),
                    },
                    {
                      label: "Total Cost Basis",
                      value: result.totalCost,
                      color: "#0891b2",
                      prefix: "₹ ",
                      displayVal: formatINR(result.totalCost),
                    },
                    {
                      label: `Tax @ ${result.taxRate}%${gainType === "ltcg" ? " (above ₹1.25L)" : ""}`,
                      value: result.taxAmount,
                      color: "#ef4444",
                      prefix: "₹ ",
                      displayVal: formatINR(result.taxAmount),
                    },
                    {
                      label: "Net Gain After Tax",
                      value: result.netGain,
                      color: "#10b981",
                      prefix: result.netGain < 0 ? "-₹ " : "₹ ",
                      displayVal: formatINR(Math.abs(result.netGain)),
                    },
                  ].map(({ label, color, prefix, displayVal }) => (
                    <div
                      key={label}
                      className="cg-result-card"
                      style={{
                        background: `linear-gradient(135deg, ${color}08, ${color}16)`,
                        borderColor: `${color}28`,
                      }}
                    >
                      <div className="cg-result-label">{label}</div>
                      <div className="cg-result-value" style={{ color }}>
                        {prefix}
                        {displayVal}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Breakdown bars */}
                {result.isProfit && (
                  <>
                    <div className="cg-section-title cg-section-title--spaced">
                      Gain Breakdown
                      <span className="cg-section-line" />
                    </div>
                    {[
                      {
                        label: "Cost Basis",
                        pct: Math.round(
                          (result.totalCost / result.saleVal) * 100,
                        ),
                        amt: result.totalCost,
                        color: "#0891b2",
                      },
                      {
                        label: "Capital Gain",
                        pct: Math.round(
                          (result.capitalGain / result.saleVal) * 100,
                        ),
                        amt: result.capitalGain,
                        color: accentColor,
                      },
                    ].map(({ label, pct, amt, color }) => (
                      <div key={label} className="cg-bar-group">
                        <div className="cg-bar-row">
                          <span className="cg-bar-label">
                            {label} ({pct}% of sale value)
                          </span>
                          <span className="cg-bar-amt">₹ {formatINR(amt)}</span>
                        </div>
                        <div className="cg-bar-track">
                          <div
                            className="cg-bar-fill"
                            style={{
                              width: `${pct}%`,
                              background: `linear-gradient(90deg, ${color}, ${color}88)`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Tax note */}
                <div
                  className="cg-tax-note"
                  style={{
                    borderColor: `${accentColor}22`,
                    background: `${accentColor}06`,
                  }}
                >
                  <FileText
                    size={14}
                    style={{ color: accentColor, flexShrink: 0 }}
                  />
                  <span>
                    {gainType === "stcg"
                      ? "STCG on listed equity / equity MFs is taxed at a flat 20% (post Union Budget July 2024). Other assets may vary."
                      : "LTCG on listed equity / equity MFs is taxed at 12.5% with an annual exemption of ₹1,25,000. Indexation not applicable for equity."}{" "}
                    <strong>Consult a tax advisor for precise figures.</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* ── Scenarios Table ── */}
            <div className="cg-card">
              <div
                className="cg-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="cg-card-body">
                <div className="cg-section-title">
                  Profitability &amp; Tax Scenarios
                  <span className="cg-section-line" />
                </div>
                <p className="cg-scenario-desc">
                  See how your tax liability and net gain would change based on
                  different sale values.
                </p>

                <div className="cg-table-wrap">
                  <table className="cg-table">
                    <thead>
                      <tr>
                        <th className="cg-th">Price Scenario</th>
                        <th className="cg-th">Sale Value (₹)</th>
                        <th className="cg-th">Gross Gain (₹)</th>
                        <th className="cg-th">Tax Liability (₹)</th>
                        <th className="cg-th">Net Gain (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.scenarios.map((row, i) => (
                        <tr
                          key={row.percent}
                          className={`${i % 2 === 0 ? "cg-tr-even" : "cg-tr-odd"}${
                            row.isCurrent ? " cg-tr-current" : ""
                          }`}
                        >
                          <td
                            className="cg-td cg-td-bold"
                            style={{
                              color: row.isCurrent ? accentColor : "#2563eb",
                            }}
                          >
                            {row.percent > 0
                              ? `+${row.percent}%`
                              : `${row.percent}%`}
                            {row.isCurrent && " (Current)"}
                          </td>
                          <td className="cg-td cg-td-mono">
                            {formatINR(row.salePrice)}
                          </td>
                          <td
                            className={`cg-td cg-td-mono ${
                              row.gain >= 0
                                ? "cg-text-success"
                                : "cg-text-danger"
                            }`}
                          >
                            {row.gain < 0 ? "-" : ""}₹{" "}
                            {formatINR(Math.abs(row.gain))}
                          </td>
                          <td className="cg-td cg-td-mono">
                            {formatINR(row.tax)}
                          </td>
                          <td
                            className={`cg-td cg-td-mono ${
                              row.net >= 0
                                ? "cg-text-success"
                                : "cg-text-danger"
                            }`}
                          >
                            {row.net < 0 ? "-" : ""}₹{" "}
                            {formatINR(Math.abs(row.net))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CTA Banner */}
        <div className="cg-cta-banner">
          <div className="cg-cta-text">
            <h3 className="cg-cta-title">
              Want to minimise your tax liability?
            </h3>
            <p className="cg-cta-sub">
              Our advisors can help you harvest losses, time your exits, and
              legally reduce your capital gains tax.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_TAX)} className="cg-cta-btn">
            Book Free Consultation →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
